import { Button } from "@/components/ui/button"
import {
  Archive,
  Download,
  EllipsisVertical,
  RotateCcw,
  Trash,
} from "lucide-react"
import { EmptyState } from "@/components/ui/empty-state"
import {
  useArchiveContentItem,
  useContentCacheStore,
  useDeleteContentItem,
  useDownloadContentItem,
  useGetLibraryArchivedContentByPage,
  useAutoTranslation,
} from "@/entities"
import { Spinner } from "@/components/ui/spinner"
import type { ContentItem } from "@/shared/api/generated"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { UiPagination } from "@/shared/ui"
import { DISCOVERED_TRANSLATION_KEYS, LANG_KEYS } from "@/shared/constants"
import styles from "./archived-list.module.scss"

const ArchivedContentItemDropdownMenu = ({
  id,
  audioUrl,
  title,
}: {
  id: string
  audioUrl: string
  title: string
}) => {
  const { t } = useAutoTranslation()
  const { handleReturn: doReturn, isLoading: isReturningLoading } =
    useArchiveContentItem()
  const { downloadItem } = useDownloadContentItem(audioUrl, title + ".mp3")
  const { handleDelete: doDelete, isLoading: isDeletingLoading } =
    useDeleteContentItem("archived")

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => doReturn(id)}>
          {isReturningLoading ? <Spinner /> : <RotateCcw />}
          {t(LANG_KEYS.RETURN)}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => doDelete(id)}>
          {isDeletingLoading ? <Spinner /> : <Trash />}
          {t(LANG_KEYS.DELETE)}
        </DropdownMenuItem>
        {audioUrl && (
          <DropdownMenuItem onClick={downloadItem}>
            <Download />
            {t(LANG_KEYS.DOWNLOAD)}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const ArchivedContentItem = ({ content }: { content: ContentItem }) => {
  return (
    <div className={styles.item}>
      <div className={styles.item__header}>
        <h4 className={styles.item__title}>{content.title}</h4>
        <ArchivedContentItemDropdownMenu
          id={content.id}
          audioUrl={content.audioUrl}
          title={content.title}
        />
      </div>
    </div>
  )
}

/**
 * Paginated list of archived items. Shared by the "Archived" library tab and
 * the header's archived modal; page state lives in useContentCacheStore so
 * both views stay in sync.
 */
export const ArchivedList = () => {
  const { t } = useAutoTranslation()
  const activeArchivedPage = useContentCacheStore(s => s.activeArchivedPage)
  const setActiveArchivedPage = useContentCacheStore(
    s => s.setActiveArchivedPage
  )
  const { data, isLoading, isFetching } =
    useGetLibraryArchivedContentByPage(activeArchivedPage)

  return (
    <>
      <div className={styles.content}>
        {(isLoading || !data || isFetching) && (
          <div className={styles.content__loading}>
            <Spinner />
          </div>
        )}

        {!isLoading && !isFetching && data && data.data.length > 0 && (
          <div className={styles.items}>
            {data.data.map(item => (
              <ArchivedContentItem
                key={`ARCHIVED_CONTENT_ITEM_${item.id}`}
                content={item}
              />
            ))}
          </div>
        )}

        {!isLoading && !isFetching && data && data.data.length === 0 && (
          <EmptyState
            icon={Archive}
            title={t(DISCOVERED_TRANSLATION_KEYS.NO_ARCHIVED_CONTENT)}
            description={t(
              DISCOVERED_TRANSLATION_KEYS.ITEMS_YOU_ARCHIVE_WILL_APPEAR_HERE_YOU_CAN_RESTORE_THEM_ANYTIME
            )}
            className="py-12"
          />
        )}
      </div>
      {data && data.meta.totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <UiPagination
            currentPage={activeArchivedPage}
            totalPages={data.meta.totalPages}
            onPageChange={setActiveArchivedPage}
          />
        </div>
      )}
    </>
  )
}
